import AxiosInstance from "./axiosInstance";
import {
    createResponseStandard,
    createErrorResponseStandard,
} from "../utils/functions";
import { getAccountInstance } from "generated/factory";
import {
    ChangePassword,
    ConfirmForgot,
    ConfirmSignUp,
    Login,
    SignUp,
} from "generated/models";
import { AxiosError, HttpStatusCode } from "axios";
import {
  CognitoUser,
  AuthenticationDetails,
  CognitoUserPool,
  CognitoUserSession
} from 'amazon-cognito-identity-js';
import { HttpStatus } from "constant";

export type AuthMFAResponse = {
    challengeName: string;
    session: CognitoUser, // return the user for follow-up MFA code verification
    challengeParameters: any,
};

export type TokenResponse = {
    IdToken: string;
    AccessToken: string;
    RefreshToken?: string;
    /** Last login timestamp */
    LastLogin: number;
}

export type LoginSession = CognitoUser;

type AuthResponse = {
    status: HttpStatusCode;
    data?: TokenResponse | AuthMFAResponse;
    error?: any // Follow the "any" type from the library
};



export class CognitoAuth {
  private userPool: CognitoUserPool;

  constructor(
    private userPoolId: string,
    private clientId: string,
  ) {

    this.userPool = new CognitoUserPool({
      UserPoolId: this.userPoolId,
      ClientId: this.clientId,
    });
  }

/**
   * Authenticates a user with the provided username and password using AWS Cognito.
   * Returns an AuthResponse containing tokens or an error status.
   *
   * @param username - The username of the user to authenticate.
   * @param password - The password of the user to authenticate.
   * @returns A Promise that resolves to an AuthResponse object.
   * @throws Will resolve with an error status if authentication fails.
   */  async authenticate(username: string, password: string): Promise<AuthResponse> {
    const userData = {
      Username: username,
      Pool: this.userPool,
    };

    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });

    const cognitoUser = new CognitoUser(userData);

    return new Promise((resolve) => {
      cognitoUser.authenticateUser(authDetails, {
        onSuccess: (session) => {
          resolve({
            status: HttpStatus.OK,
            data: {
                AccessToken: session.getAccessToken().getJwtToken(),
                IdToken: session.getIdToken().getJwtToken(),
                RefreshToken: session.getRefreshToken().getToken(),
                LastLogin: session.getAccessToken().getExpiration(),
            },
          });
        },
        onFailure: (err) => {
          resolve({ status: HttpStatus.BAD_REQUEST, error: err });
        },
        mfaRequired: (challengeName, challengeParameters) => {
          // MFA challenge triggered
          resolve({
            status: HttpStatus.OK,
            data: {
              challengeName,
              session: cognitoUser,
              challengeParameters,
            },
          });
        },
      });
    });
  }

/**
   * Sends a multi-factor authentication (MFA) code to the specified Cognito user.
   * Resolves with an AuthResponse containing tokens on success or an error on failure.
   *
   * @param cognitoUser - The CognitoUser instance to send the MFA code for.
   * @param mfaCode - The MFA code to be sent.
   * @returns A Promise that resolves to an AuthResponse object.
   * @throws Will resolve with an error object if the MFA code sending fails.
   */  async sendMfaCode(
    cognitoUser: CognitoUser,
    mfaCode: string,
  ): Promise<AuthResponse> {
    return new Promise((resolve) => {

      cognitoUser.sendMFACode(mfaCode, {
        onSuccess: (session: CognitoUserSession) => {
          resolve({
            status: HttpStatus.OK,
            data: {
                AccessToken: session.getAccessToken().getJwtToken(),
                IdToken: session.getIdToken().getJwtToken(),
                RefreshToken: session.getRefreshToken().getToken(),
                LastLogin: session.getAccessToken().getExpiration(),
            },
          });
        },
        onFailure: (err) => {
          resolve({ status: HttpStatus.BAD_REQUEST, error: err });
        },
      });
    });
  }
}


export class AuthenticationService {
    private accountClient = getAccountInstance(AxiosInstance);
    private cognitoAuth: CognitoAuth = new CognitoAuth(
        process.env.REACT_APP_AWS_POOL_ID || "",
        process.env.REACT_APP_AWS_CLIENT_ID || "",
    );

    login = async ({ email, password }: Login): Promise<AuthResponse> => {
        return await this.cognitoAuth.authenticate(email, password);
    };

    sendForgotPasswordEmail = async (email: string) => {
        try {
            const response = await this.accountClient.forgotPassword({
                email,
            });
            return createResponseStandard(response);
        } catch (error) {
            return createErrorResponseStandard(error as AxiosError);
        }
    };

    changePassword = async (body: ConfirmForgot) => {
        try {
            const response =
                await this.accountClient.confirmForgotPasswordWithCode(body);
            return createResponseStandard(response);
        } catch (error) {
            return createErrorResponseStandard(error as AxiosError);
        }
    };

    signUp = async (body: SignUp) => {
        try {
            const response = await this.accountClient.signUp(body);
            return createResponseStandard(response);
        } catch (error) {
            return createErrorResponseStandard(error as AxiosError);
        }
    };

    submitSignUpVerificationCode = async (body: ConfirmSignUp) => {
        try {
            const response = await this.accountClient.confirmSighUpWithCode(
                body
            );
            return createResponseStandard(response);
        } catch (error) {
            return createErrorResponseStandard(error as AxiosError);
        }
    };


    submitMFACode = async (session: CognitoUser,  mfaCode: string) => {
        return await this.cognitoAuth.sendMfaCode(
            session,
            mfaCode
        );
    };

    changeProfilePassword = async (body: ChangePassword) => {
        try {
            const response = await this.accountClient.changePassword(body);
            return createResponseStandard(response);
        } catch (error) {
            return createErrorResponseStandard(error as AxiosError);
        }
    };

    resendConfirmationCode = async (email: string) => {
        try {
            const response = await this.accountClient.reSendConfirmationCode({
                email: email,
            });
            return createResponseStandard(response);
        } catch (error) {
            return createErrorResponseStandard(error as AxiosError);
        }
    };
}

const authService = new AuthenticationService();
export default authService;